import { getUser, getUserByEmail } from '@modules/supabase/users';
import { Typography, PaperProps, Stack, Box, Tooltip, Alert } from '@mui/material';
import { EventMembership, STATUS, User, UserTeam } from '@type/db-types';
import { breadcrumb, log } from '@utils/logging';
import { useCallback, useEffect, useState } from 'react';
import RenderStatusTag from './RenderStatusTag';
import { getUserEventMembership } from '@modules/supabase/events';
import { getTeamMembershipInfo } from '@modules/supabase/teams';
import { PALLETTE } from '@modules/theme';
import { Tag } from 'antd';
import LoadingPaper from './LoadingPaper';
import useAuth from '@hooks/useAuth';
import useWatchStripeAccount from '@hooks/useWatchStripeAccount';
import { useFormContext } from 'react-hook-form';
import { EventForm } from '@components/eventCreation/CreateEventUtils';

interface Props extends PaperProps {
    email?: string;
    parentEntityId: string;
    payToUserId?: string;
    parentEntityTable: 'event' | 'team';
}

export default function StripeUserPreview({
    email,
    parentEntityId,
    payToUserId,
    parentEntityTable,
}: Props) {
    const { authUser } = useAuth();
    const [user, setUser] = useState<User | null>(
        payToUserId ? ({ id: payToUserId } as User) : null,
    );
    const [membership, setMembership] = useState<UserTeam | EventMembership | null>(null);
    const [loading, setLoading] = useState(true);
    const creationMode = !parentEntityId;
    const { stripeInfo, loading: stripeLoading } = useWatchStripeAccount(
        user?.id,
        `StripeUserPreview ${parentEntityTable.toUpperCase()} parentEntityId: ${parentEntityId} payToUserId: ${payToUserId}`,
    );
    const { setValue } = useFormContext<EventForm>();

    const selectedSelf = payToUserId === authUser?.id;
    // paytouserid is correct, rendering incorrect info
    const getUserMembership = useCallback(
        (userId: string): Promise<unknown> => {
            // if we are in an event context, use event, else use team
            try {
                if (parentEntityTable === 'event') {
                    return getUserEventMembership(parentEntityId, userId).then(setMembership);
                } else if (parentEntityId === 'team') {
                    return getTeamMembershipInfo(parentEntityId, userId, true).then(setMembership);
                } else {
                    log(`[StripeUserPreview:getUserMembership] no event or team context`);
                    setMembership(null);
                    return Promise.resolve(null);
                }
            } catch (e) {
                log(`[StripeUserPreview:getUserMembership] error`, e);
                setMembership(null);
                return Promise.resolve(null);
            }
        },
        [parentEntityId, parentEntityTable],
    );

    useEffect(() => {
        if (email) {
            getUserByEmail(email)
                .then(setUser)
                .catch(() => setLoading(false))
                .finally(() => setLoading(false));
        } else if (payToUserId) {
            console.log(`[StripeUserPreview] getting user by id: ${payToUserId}`);
            getUser(payToUserId)
                .then(setUser)
                .catch(() => setLoading(false))
                .finally(() => setLoading(false));
        } else {
            console.log('[StripeUserPreview] no email or payToUserId');
            setLoading(false);
        }
    }, [
        email,
        payToUserId,
        stripeInfo?.stripeId,
        stripeInfo?.detailsSubmitted,
        stripeInfo?.allRequirementsSubmitted,
    ]);

    useEffect(() => {
        console.log('[StripeUserPreview] stripeInfo changed', {
            userId: user?.id,
            payToUserId,
            stripeUserId: stripeInfo?.id,
            stripeInfo,
            email,
        });
        setValue(
            'stripeDetailsSubmitted',
            !!(stripeInfo?.detailsSubmitted || stripeInfo?.allRequirementsSubmitted),
        );
        // eslint-disable-next-line react-compiler/react-compiler
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stripeInfo?.detailsSubmitted, stripeInfo?.allRequirementsSubmitted, setValue]);

    useEffect(() => {
        if (user?.id) {
            getUserMembership(user.id);
        }
    }, [getUserMembership, user?.id]);

    useEffect(() => {
        if (payToUserId && email) {
            log(`[StripeUserPreview] email & userId: ${email}`, { payToUserId, email });
        }
    }, [email, payToUserId]);

    const readyForStripe =
        !!stripeInfo?.stripeId &&
        !!stripeInfo?.detailsSubmitted &&
        !!stripeInfo?.allRequirementsSubmitted;

    const { alert, severity, backgroundColor, borderColor } = readyForStripe
        ? SUCCESS_CONFIG
        : ERROR_CONFIG;

    if (loading || stripeLoading) {
        console.log('StripeUserPreview', { user, membership, stripeInfo, loading, stripeLoading });
        breadcrumb('StripeUserPreview', { loading, stripeLoading });
    }

    return (
        <LoadingPaper loading={loading || stripeLoading} message="Checking payments setup">
            <Stack spacing={2} alignItems="center" marginTop="1rem">
                <Alert severity={severity} sx={{ marginBottom: 1 }}>
                    <Typography variant="body2">{alert}</Typography>
                </Alert>
                <Stack
                    spacing={2}
                    alignItems="center"
                    marginTop="1rem"
                    sx={{
                        backgroundColor,
                        borderRadius: '1rem',
                        padding: '1rem',
                        border: `1px solid ${borderColor}`,
                    }}
                >
                    <Typography variant="h6">
                        Payment Account{selectedSelf ? ' (you)' : ''}:
                    </Typography>
                    <Stack spacing={1}>
                        <Box display="flex">
                            <Typography fontWeight="bold" marginRight="1rem">
                                Name:
                            </Typography>
                            <Typography>
                                {user ? `${user.firstName || ''} ${user.lastName || ''}` : null}
                            </Typography>
                        </Box>
                        <Box display="flex">
                            <Typography fontWeight="bold" marginRight="1rem">
                                Email:
                            </Typography>
                            <Typography>{user?.email}</Typography>
                        </Box>
                        <Tooltip title="User's account status in our system" placement="right">
                            <Box display="flex" justifyContent="space-between">
                                <Typography fontWeight="bold" marginRight="1rem">
                                    BoutPro Account:
                                </Typography>
                                <RenderStatusTag
                                    membershipStatus={user?.status || ('Not member yet' as STATUS)}
                                />
                            </Box>
                        </Tooltip>
                        {creationMode ? null : ( // if we are creating a new entity, we don't need to show membership
                            <Tooltip
                                title="User's membership in this entity you're editing now"
                                placement="right"
                            >
                                <Box display="flex" justifyContent="space-between">
                                    <Typography fontWeight="bold" marginRight="1rem">
                                        User Membership:
                                    </Typography>
                                    <RenderStatusTag
                                        membershipStatus={membership?.status || ('none' as STATUS)}
                                    />
                                </Box>
                            </Tooltip>
                        )}
                        <Tooltip title="User's connected stripe account" placement="right">
                            <Box display="flex" justifyContent="space-between">
                                <Typography fontWeight="bold" marginRight="1rem">
                                    Stripe Account:
                                </Typography>
                                {stripeInfo?.stripeId ? (
                                    <Tag color="success">Created</Tag>
                                ) : (
                                    <Tag color="error">Not created yet</Tag>
                                )}
                            </Box>
                        </Tooltip>
                        <Tooltip title="Initial stripe onboarding" placement="right">
                            <Box display="flex" justifyContent="space-between">
                                <Typography fontWeight="bold" marginRight="1rem">
                                    Stripe Onboarding:
                                </Typography>
                                {stripeInfo?.detailsSubmitted ? (
                                    <Tag color="success">Completed</Tag>
                                ) : (
                                    <Tag color="error">Incomplete</Tag>
                                )}
                            </Box>
                        </Tooltip>
                        <Tooltip
                            title="Additional stripe requirements for tax reporting, etc..."
                            placement="right"
                        >
                            <Box display="flex" justifyContent="space-between">
                                <Typography fontWeight="bold" marginRight="1rem">
                                    Stripe Requirements:
                                </Typography>
                                {stripeInfo?.allRequirementsSubmitted ? (
                                    <Tag color="success">Completed</Tag>
                                ) : (
                                    <Tag color="error">Incomplete</Tag>
                                )}
                            </Box>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Stack>
        </LoadingPaper>
    );
}

const ERROR_CONFIG = {
    backgroundColor: PALLETTE.error[50],
    borderColor: PALLETTE.error[600],
    alert: 'Review and complete requirements to accept payments',
    severity: 'error' as const,
};
const SUCCESS_CONFIG = {
    backgroundColor: PALLETTE.primary[50],
    borderColor: PALLETTE.primary[800],
    alert: 'You are ready to accept payments',
    severity: 'success' as const,
};
